import { useState } from 'react';

import { InformationCircleIcon } from '@heroicons/react/20/solid';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';

type PastDueBannerProps = {
  group: GroupSubscriptionData;
  /**
   * Host action URL the CTA posts to. The button is a real `<form>` submit (not
   * a JS handler) so the browser does a native navigation: the host action mints
   * a fresh Stripe billing-portal session and 302s to it, and the tab's own
   * loading affordance covers the POST + the external redirect. Stripe portal
   * URLs are single-use + short-lived, so they MUST be minted at click time —
   * never fetched ahead and held. Omit to render the banner without the CTA
   * (Dream Builder preview / graceful degradation).
   */
  updatePaymentMethodAction?: string;
};

/**
 * Shown while a group subscription is `past_due` (Stripe is retrying the
 * renewal charge). Seat management stays enabled — members keep access during
 * the retry window — so this is purely a "fix your card" nudge, not a lockout.
 */
export function PastDueBanner({ group, updatePaymentMethodAction }: PastDueBannerProps) {
  // Guards against a double-submit minting two Stripe portal sessions during the
  // redirect window. Disable on `submit` (not click) so the native submission
  // has already fired before the button goes disabled — blocking the second
  // click without cancelling the first.
  const [submitting, setSubmitting] = useState(false);

  if (group.status !== 'past_due') return null;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-3">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <p className="font-medium">There's a billing issue with your group subscription</p>
          <p>Update your payment method to keep your members' access.</p>
        </div>
      </div>
      {updatePaymentMethodAction && (
        <form
          method="post"
          action={updatePaymentMethodAction}
          onSubmit={() => setSubmitting(true)}
          className="flex-shrink-0 self-start sm:self-center"
        >
          <input type="hidden" name="group_subscription_id" value={group.id} />
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-900 shadow-sm hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Update payment method
          </button>
        </form>
      )}
    </div>
  );
}
